import { Component, EventEmitter, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PERMISSION_NAME, PermissionService } from '@ebw-monorepo/utils';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import moment from 'moment';
import { Subscription, debounceTime } from 'rxjs';
import { FiberStatusEnum, Fibra } from '../../../../models/fibra.model';
import { BaseTableComponent } from '../base-table.component';
import { FiberEditComponent } from '../fiber-edit/fiber-edit.component';
import { FiberHistoryModalComponent } from '../fiber-history-modal/fiber-history-modal.component';

@Component({
  selector: 'ebw-monorepo-fiber-table',
  templateUrl: './fiber-table.component.html',
  styleUrls: ['./fiber-table.component.scss']
})
export class FiberTableComponent extends BaseTableComponent<Fibra> implements OnInit, OnDestroy {

  @Output() fetchDataEventEmitter = new EventEmitter<any>();

  override pageSize = 24;
  override pageSizeOptions = [24, 48, 144, 288];

  // template reference to render the info column
  @ViewChild('actionsTemplate', { static: true })
  actionsTemplate!: TemplateRef<any>;

  @ViewChild('statusColTemplate', { static: true })
  statusColTemplate!: TemplateRef<any>;

  fiberStatusEnum = FiberStatusEnum;
  public statoFibra = [
    {
      value: null,
      label: ''
    },
    {
      value: FiberStatusEnum.free,
      label: this._translateService.instant('map.sidebar.fibra.status.free')
    },
    {
      value: FiberStatusEnum.booked,
      label: this._translateService.instant('map.sidebar.fibra.status.booked')
    },
    {
      value: FiberStatusEnum.unavailable,
      label: this._translateService.instant('map.sidebar.fibra.status.unavailable')
    },
    {
      value: FiberStatusEnum.outOfOrder,
      label: this._translateService.instant('map.sidebar.fibra.status.out_of_order')
    },
    {
      value: FiberStatusEnum.bookedLD,
      label: this._translateService.instant('map.sidebar.fibra.status.booked_ld')
    },
    {
      value: FiberStatusEnum.unusable,
      label: this._translateService.instant('map.sidebar.fibra.status.unusable')
    },
    {
      value: FiberStatusEnum.toCheck,
      label: this._translateService.instant('map.sidebar.fibra.status.to_check')
    }
  ];
  searchFilter: FormGroup;
  userCanEdit = false;
  private _filterSubscribe: Subscription;

  

  // --------------------------------------------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private _dialog: MatDialog,
    private _fb: FormBuilder,
    private _apiUrlService: ApiUrlService,
    private _permissionService: PermissionService
  ) {
    super();
    this._exportFilename = 'export-fibre';
  }

  // --------------------------------------------------------------------------------------------------------------------

  override ngOnInit(): void {
    super.ngOnInit();
    this.userCanEdit = this._permissionService.checkPermission(PERMISSION_NAME.FIBER_JOINTS.RACCORDI_SALE_EDIT_WEB);

    this._exportApi = {
      csv: this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV),
      html: this._apiUrlService.createUrl(API_URLS.MAP.EXPORT.HTML)
    };

    this.initFilterForm();
  }

  ngOnDestroy(): void {
    this._filterSubscribe.unsubscribe();
  }

  // ------------------------------------------------------------------------------------------------------------

  initializeTable(fromTabChange = false) {
    this.columnsGrid = [
      {
        header: this._translateService.instant('map.sidebar.fibra.table.number_fibra'),
        field: 'fiber_number',
        sortable: true,
        width: '100px',
        pinned: 'left'
      },
      // {
      //   header: this._translateService.instant('map.sidebar.fibra.table.id_gis'),
      //   field: 'id_gis',
      //   sortable: false,
      //   pinned: 'left'
      // },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.stato'),
        field: 'stato',
        sortable: false,
        cellTemplate: this.statusColTemplate
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.circuito_td'),
        field: 'circuito_td',
        sortable: false
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.codice_servizio_tecnico'),
        field: 'codice_servizio_tecnico',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.cliente'),
        field: 'cliente',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.porte_apparato'),
        field: 'porte_apparato',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.porte_apparato_2'),
        field: 'porte_apparato_2',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.segment_rc'),
        field: 'segment_rc',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.note'),
        field: 'note',
        sortable: true
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.user'),
        field: 'updated_by'
      },
      {
        header: this._translateService.instant('map.sidebar.fibra.table.updated_at'),
        field: 'updated_at',
        formatter: (data: any) =>
          `<span class="date-field">${
            data.updated_at ? moment(data.updated_at).format('DD/MM/YYYY HH:mm') : '-'
          }</span>`
      },
      {
        header: 'Azioni',
        field: 'info',
        cellTemplate: this.actionsTemplate,
        width: '88px',
        pinned: 'right'
      }
    ];
  }

  // ------------------------------------------------------------------------------------------------------------
  getFilters(limit = this.pageSize, offset = this.offset) {
    let nextFilters = {
      limit,
      offset
    };

    if (this._currentSort) {
      nextFilters['order_by'] = this._currentSort.field;
      nextFilters['order'] = this._currentSort.direction;
    }

    let filters = this.config.filters;

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          return;
        }
        nextFilters[key] = filters[key];
      });
    }

    return nextFilters;
  }

  fetchData(customFilter?: Record<string, any>) {
    this.resetComponentProperties();
    let filters = { ...this.getFilters(), ...customFilter };

    this._utilsService
      .postRequest(
        this._apiUrlService.createUrl(API_URLS.FIBER_JOINTS.FIBER.LIST),
        filters,
        false,
        {},
        'application/json'
      )
      .subscribe({
        next: (resp: any) => {
          this.totalCount = resp.numRows;
          this.dataGrid = resp.records;

          this.fetchDataEventEmitter.emit();
        },
        error: (err) => {
          this._ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: err.message
          });
        }
      });
  }

  openFibraModal(id = null) {
    if (!this.userCanEdit) {
      return;
    }

    let ref = this._dialog.open(FiberEditComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '70%',
      data: {
        id
      }
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp) {
        this.fetchData();
      }
    });
  }

  openHistoryModal(fibra = null) {
    let ref = this._dialog.open(FiberHistoryModalComponent, {
      maxWidth: '95vw',
      maxHeight: '85vh',
      width: '70%',
      data: {
        fibra
      }
    });

    // ref.afterClosed().subscribe((resp) => {
    //   if (resp) {
    //     this.fetchData();
    //   }
    // });
  }

  formatExportData(data: any[], excel: boolean = false) {
    return this.defaultFormatData(data, excel);
  }

  initFilterForm() {
    this.searchFilter = this._fb.group({
      term: [''],
      status: [null]
    });

    this._filterSubscribe = this.searchFilter.valueChanges.pipe(debounceTime(300)).subscribe((val) => {
      let newFilter = {};

      if (!!val.term) {
        newFilter = {
          // id_gis: val.term,
          // id_fibra_gis: val.term,
          fiber_number: val.term,
          cliente: val.term,
          circuito_td: val.term,
          codice_servizio_tecnico: val.term,
          porte_apparato: val.term,
          porte_apparato_2: val.term,
          segment_rc: val.term,
          note: val.term
        };
      }

      if (val.status) {
        newFilter['stato'] = val.status;
      }

      this.currentPageIndex = 0;
      this.offset = this.currentPageIndex * this.pageSize;

      this.fetchData(newFilter);
    });
  }

  clearDefaultSearchFilter() {
    this.searchFilter.reset();
    this.fetchData();
  }
}
